/**
 * Pure helpers for the Agent Bar's message list. The component owns the
 * actual state + SSE plumbing; this module isolates the reducer rules so
 * they're testable without rendering or network mocking.
 */

export type ChatRole = 'user' | 'assistant';

/** A generated image attached to an assistant turn (image-gen mode). */
export interface ChatImage {
  url: string;
  width?: number;
  height?: number;
}

export interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  isStreaming?: boolean;
  isError?: boolean;
  /** Present on assistant turns produced by an image provider (e.g. fal.ai). */
  images?: ChatImage[];
}

/**
 * Append a user message and a placeholder streaming assistant message in
 * one step — the assistant message accumulates tokens as they arrive.
 * Returns both new ids so the caller can target them in subsequent updates.
 */
export function startTurn(
  messages: ChatMessage[],
  userContent: string,
  idGen: () => string = defaultIdGen,
): { next: ChatMessage[]; userId: string; assistantId: string } {
  const userId = idGen();
  const assistantId = idGen();
  const next = [
    ...messages,
    { id: userId, role: 'user' as const, content: userContent },
    { id: assistantId, role: 'assistant' as const, content: '', isStreaming: true },
  ];
  return { next, userId, assistantId };
}

/**
 * Append a token to the in-flight assistant message identified by `id`.
 * No-op if the id doesn't match (e.g. the user already cleared the
 * conversation while a stream was still flushing late events).
 */
export function appendToken(messages: ChatMessage[], assistantId: string, token: string): ChatMessage[] {
  const idx = messages.findIndex(m => m.id === assistantId);
  if (idx === -1) return messages;
  const updated = { ...messages[idx], content: messages[idx].content + token };
  return [...messages.slice(0, idx), updated, ...messages.slice(idx + 1)];
}

/**
 * Mark the assistant message as no longer streaming. Used when the SSE
 * stream emits `done` or the request completes. Idempotent.
 */
export function finalizeAssistant(messages: ChatMessage[], assistantId: string): ChatMessage[] {
  const idx = messages.findIndex(m => m.id === assistantId);
  if (idx === -1) return messages;
  if (!messages[idx].isStreaming) return messages;
  const updated = { ...messages[idx], isStreaming: false };
  return [...messages.slice(0, idx), updated, ...messages.slice(idx + 1)];
}

/**
 * Finalize the in-flight assistant message with generated images (image-gen
 * mode). Stops the streaming indicator and attaches the images; `content`
 * becomes a short caption (or stays whatever partial text was set). No-op if
 * the id doesn't match.
 */
export function finalizeAssistantWithImages(
  messages: ChatMessage[],
  assistantId: string,
  images: ChatImage[],
  caption = '',
): ChatMessage[] {
  const idx = messages.findIndex(m => m.id === assistantId);
  if (idx === -1) return messages;
  const updated: ChatMessage = {
    ...messages[idx],
    content: caption || messages[idx].content,
    images,
    isStreaming: false,
  };
  return [...messages.slice(0, idx), updated, ...messages.slice(idx + 1)];
}

/**
 * Mark the assistant message as errored, replacing its content with the
 * provided error string and stopping the streaming indicator. Used when
 * the SSE stream emits `error` or the fetch throws.
 */
export function markAssistantError(
  messages: ChatMessage[],
  assistantId: string,
  errorMessage: string,
): ChatMessage[] {
  const idx = messages.findIndex(m => m.id === assistantId);
  if (idx === -1) return messages;
  const updated: ChatMessage = {
    ...messages[idx],
    content: messages[idx].content ? `${messages[idx].content}\n\n_${errorMessage}_` : `_${errorMessage}_`,
    isStreaming: false,
    isError: true,
  };
  return [...messages.slice(0, idx), updated, ...messages.slice(idx + 1)];
}

/**
 * Convert ChatMessage[] into the BE-expected `context.history` shape —
 * a JSON array of `{role, content}` objects. Drops the trailing
 * streaming assistant message (it's the one we're *about to* generate),
 * and strips error markers so the BE doesn't see internal markup.
 */
export function buildHistoryForBackend(messages: ChatMessage[]): { role: ChatRole; content: string }[] {
  return messages
    .filter(m => !m.isStreaming && !m.isError)
    .map(m => ({ role: m.role, content: m.content }));
}

let counter = 0;
function defaultIdGen(): string {
  counter += 1;
  return `chat-${Date.now()}-${counter}`;
}

// ----- F16b: per-studio conversation persistence + context awareness -----

/** Rolling cap. Older turns get dropped first when the conversation grows. */
export const CHAT_HISTORY_TURN_CAP = 50;

/**
 * localStorage key for a (labId, studioId) conversation. Returns null when
 * either input is empty — the caller should skip persistence in that case
 * (e.g. studio metadata still loading).
 */
export function chatHistoryStorageKey(labId: string | undefined, studioId: string | undefined): string | null {
  const lid = (labId ?? '').trim();
  const sid = (studioId ?? '').trim();
  if (!lid || !sid) return null;
  return `${CHAT_HISTORY_PREFIX}${lid}__${sid}`;
}

const CHAT_HISTORY_PREFIX = 'ct_v3_chat_history__';

/**
 * Drops every saved conversation. They're keyed by lab and studio, not by user,
 * and are sent back to the model as history — so they belong to whoever was
 * signed in, and must not carry over to the next account in this browser.
 */
export function clearAllConversations(store: Pick<Storage, 'length' | 'key' | 'removeItem'> | null): void {
  if (!store) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (key?.startsWith(CHAT_HISTORY_PREFIX)) keys.push(key);
    }
    keys.forEach(key => store.removeItem(key));
  } catch {
    // Blocked storage — nothing was persisted to clear.
  }
}

/** Minimal storage interface for testability — Window.localStorage satisfies it. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Load a saved conversation. Returns `[]` on any parse failure or shape
 * mismatch — silently swallowing errors keeps the chat usable when an
 * older app version wrote an incompatible shape. Strips `isStreaming` so
 * a tab that crashed mid-stream doesn't restore a phantom in-flight turn.
 */
export function loadConversation(key: string, store: KeyValueStore | null): ChatMessage[] {
  if (!store) return [];
  let raw: string | null;
  try {
    raw = store.getItem(key);
  } catch {
    return [];
  }
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const valid: ChatMessage[] = [];
    for (const item of parsed) {
      if (
        item &&
        typeof item === 'object' &&
        typeof item.id === 'string' &&
        (item.role === 'user' || item.role === 'assistant') &&
        typeof item.content === 'string'
      ) {
        // Strip in-flight markers — a previously-streaming turn is just
        // a final turn now (whatever content survived the page unload).
        // Note: persisted image URLs are signed and may have expired by the
        // time an old thread is reloaded; the <img> simply fails to load in
        // that case (acceptable for v1 — no broken-state cleanup).
        const images = Array.isArray(item.images)
          ? item.images.filter(
              (im: unknown): im is ChatImage =>
                !!im && typeof im === 'object' && typeof (im as ChatImage).url === 'string',
            )
          : undefined;
        valid.push({
          id: item.id,
          role: item.role,
          content: item.content,
          ...(item.isError ? { isError: true } : {}),
          ...(images && images.length > 0 ? { images } : {}),
        });
      }
    }
    return valid;
  } catch {
    return [];
  }
}

/**
 * Save a conversation. Drops in-flight (`isStreaming`) messages — those
 * are mid-turn and shouldn't persist. Caps the saved tail at
 * `CHAT_HISTORY_TURN_CAP` (newest wins). Errors during write (quota
 * exceeded, private browsing, etc.) are swallowed silently.
 */
export function saveConversation(
  key: string,
  messages: ChatMessage[],
  store: KeyValueStore | null,
): void {
  if (!store) return;
  const persistable = messages.filter(m => !m.isStreaming);
  const capped =
    persistable.length > CHAT_HISTORY_TURN_CAP
      ? persistable.slice(persistable.length - CHAT_HISTORY_TURN_CAP)
      : persistable;
  try {
    if (capped.length === 0) {
      store.removeItem(key);
    } else {
      store.setItem(key, JSON.stringify(capped));
    }
  } catch {
    // localStorage is best-effort; chat still works in-memory if persistence fails.
  }
}

/**
 * Build the `context` object the BE chat endpoint consumes (B24a). When
 * `studio`, `activeOperation`, or any of the elevation-pass fields (swatch
 * selections, filter state, active asset, op params) are present, the LLM
 * gets lab-aware grounding — the controller formats them into a
 * "Current user context" prelude on the system prompt.
 *
 * Elevation pass 2026-05-26 (Area 3) closed the gap where the FE had been
 * threading lab/studio/op-kind only and the BE was discarding everything
 * other than provider/model/history. See
 * `creative-toolbox-v3-memory/18-elevation-pass-2026-05-26.md` Area 3.
 */
export interface ChatContextInputs {
  provider: string;
  model: string;
  history: { role: ChatRole; content: string }[];
  lab?: { id?: string; name?: string };
  studio?: { id?: string; name?: string };
  activeOperationKind?: string;
  /**
   * Studio palette → active swatch id map. Lifted from ImageLab's
   * `swatchSelections` state. Lets the LLM reference what the user has
   * picked ("Why is my Ankara + Streetwear combo failing?").
   */
  swatchSelections?: Record<string, string>;
  /**
   * Snapshot of the 8-slider CSS-filter preview state (brightness, contrast,
   * saturation, hue, blur, grayscale, sepia, invert). Only included when
   * any slider deviates from the default — the controller drops it from
   * the prompt prelude when the user hasn't touched the filters.
   */
  filterState?: Record<string, number> | null;
  /**
   * Id of the asset currently shown in the canvas carousel. Lets the LLM
   * cross-reference user questions to lineage / lifecycle queries. The
   * signed URL is intentionally NOT included (vision-aware chat is the
   * deferred C3 slice; today's models can't see the asset).
   */
  activeAssetId?: string;
  /**
   * Parameter values from the right-rail op panel's current form (prompt,
   * negativePrompt, etc.) so the LLM knows what the user is composing.
   * Caller is responsible for redaction — anything in here will be visible
   * to the LLM provider.
   */
  activeOperationParams?: Record<string, unknown>;
  /**
   * Current document text from the workspace canvas (Writer's Lab Lexical
   * editor). Lets the user say "critique the blog above" / "tighten the
   * paragraph I just wrote" without re-pasting. Capped at
   * {@link DOCUMENT_CONTEXT_CHAR_CAP} to protect token budget; longer
   * docs are truncated with a `[...truncated]` marker.
   *
   * Image Lab doesn't have a document surface — leave undefined.
   */
  currentDocument?: string;
}

/**
 * Hard cap on the document text we ship inside chat context. Sized to fit
 * comfortably in a 4k-token system-prompt budget while accommodating most
 * blog-length drafts. Longer documents get a truncation marker appended so
 * the LLM knows it didn't see the whole thing.
 */
export const DOCUMENT_CONTEXT_CHAR_CAP = 32000;

export function buildChatContext(
  inputs: ChatContextInputs,
): Record<string, unknown> {
  const ctx: Record<string, unknown> = {
    provider: inputs.provider,
    model: inputs.model,
    history: inputs.history,
  };
  if (inputs.lab?.id) {
    ctx.labId = inputs.lab.id;
    if (inputs.lab.name) ctx.labName = inputs.lab.name;
  }
  if (inputs.studio?.id) {
    ctx.studioId = inputs.studio.id;
    if (inputs.studio.name) ctx.studioName = inputs.studio.name;
  }
  if (inputs.activeOperationKind) {
    ctx.activeOperationKind = inputs.activeOperationKind;
  }
  if (inputs.swatchSelections && Object.keys(inputs.swatchSelections).length > 0) {
    ctx.swatchSelections = inputs.swatchSelections;
  }
  if (inputs.filterState && hasNonDefaultFilters(inputs.filterState)) {
    ctx.filterState = inputs.filterState;
  }
  if (inputs.activeAssetId) {
    ctx.activeAssetId = inputs.activeAssetId;
  }
  if (inputs.activeOperationParams && Object.keys(inputs.activeOperationParams).length > 0) {
    ctx.activeOperationParams = inputs.activeOperationParams;
  }
  if (inputs.currentDocument && inputs.currentDocument.trim().length > 0) {
    const raw = inputs.currentDocument.trim();
    ctx.currentDocument =
      raw.length <= DOCUMENT_CONTEXT_CHAR_CAP
        ? raw
        : `${raw.slice(0, DOCUMENT_CONTEXT_CHAR_CAP)}\n\n[...truncated, original was ${raw.length} characters]`;
  }
  return ctx;
}

/**
 * Default CSS-filter values — mirrors `DEFAULT_FILTERS_STATE` without
 * importing it (this helpers module deliberately stays free of Image Lab
 * domain coupling). True when any value deviates from the defaults.
 */
const FILTER_DEFAULTS: Record<string, number> = {
  brightness: 100,
  contrast: 100,
  saturation: 100,
  hue: 0,
  blur: 0,
  grayscale: 0,
  sepia: 0,
  invert: 0,
};

function hasNonDefaultFilters(state: Record<string, number>): boolean {
  for (const [key, value] of Object.entries(state)) {
    const def = FILTER_DEFAULTS[key];
    if (def === undefined) {
      // Unknown filter key — include it so the LLM can reason about novel
      // filters added in future releases.
      return true;
    }
    if (value !== def) return true;
  }
  return false;
}
